/**
 * Stock browse — typed fetch wrappers and URL <-> state helpers.
 *
 * The list endpoint is implemented by the BFF (see
 * api/src/routes/portal/stock/list.ts, Agent K). Both endpoints flow
 * through the same `portalApi()` (session cookie + CSRF double-submit).
 */
import { portalApi } from './apiClient';
import {
  STOCK_FILTER_DEFAULTS,
  STOCK_SORTS,
  isStockSort,
  type StockDetailResponse,
  type StockListFilters,
  type StockListQueryState,
  type StockListResponse,
  type StockSort,
} from './stockTypes';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 20;
const MAX_PER_PAGE = 100;

/* ----------------------------- URL encoding ---------------------------- */

function csv(values: readonly string[]): string | undefined {
  if (values.length === 0) return undefined;
  return values.join(',');
}

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function parsePositiveInt(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

export function parseQueryToState(search: string | URLSearchParams): StockListQueryState {
  const sp = typeof search === 'string' ? new URLSearchParams(search) : search;
  const sortRaw = sp.get('sort');
  const sort: StockSort = isStockSort(sortRaw) ? sortRaw : STOCK_SORTS[0];
  const pageRaw = parsePositiveInt(sp.get('page'));
  const perPageRaw = parsePositiveInt(sp.get('per_page'));
  return {
    ...STOCK_FILTER_DEFAULTS,
    q: sp.get('q') ?? '',
    makerEn: parseCsv(sp.get('maker_en')),
    bodytypeCd: parseCsv(sp.get('bodytype_cd')),
    fuelCd: parseCsv(sp.get('fuel_cd')),
    driveCd: parseCsv(sp.get('drive_cd')),
    missionCd: parseCsv(sp.get('mission_cd')),
    prefName: parseCsv(sp.get('pref_name')),
    yearMin: parsePositiveInt(sp.get('year_min')),
    yearMax: parsePositiveInt(sp.get('year_max')),
    priceMin: parsePositiveInt(sp.get('price_min')),
    priceMax: parsePositiveInt(sp.get('price_max')),
    mileageMax: parsePositiveInt(sp.get('mileage_max')),
    auctionGradeMin: sp.get('auction_grade_min'),
    auctionGradeMax: sp.get('auction_grade_max'),
    page: pageRaw ?? DEFAULT_PAGE,
    perPage: perPageRaw === null ? DEFAULT_PER_PAGE : Math.min(perPageRaw, MAX_PER_PAGE),
    sort,
  };
}

export function stateToQueryString(state: StockListQueryState): string {
  const sp = new URLSearchParams();
  if (state.q.trim()) sp.set('q', state.q.trim());
  const append = (k: string, v: string | undefined): void => {
    if (v !== undefined && v !== '') sp.set(k, v);
  };
  append('maker_en', csv(state.makerEn));
  append('bodytype_cd', csv(state.bodytypeCd));
  append('fuel_cd', csv(state.fuelCd));
  append('drive_cd', csv(state.driveCd));
  append('mission_cd', csv(state.missionCd));
  append('pref_name', csv(state.prefName));
  if (state.yearMin !== null) sp.set('year_min', String(state.yearMin));
  if (state.yearMax !== null) sp.set('year_max', String(state.yearMax));
  if (state.priceMin !== null) sp.set('price_min', String(state.priceMin));
  if (state.priceMax !== null) sp.set('price_max', String(state.priceMax));
  if (state.mileageMax !== null) sp.set('mileage_max', String(state.mileageMax));
  if (state.auctionGradeMin) sp.set('auction_grade_min', state.auctionGradeMin);
  if (state.auctionGradeMax) sp.set('auction_grade_max', state.auctionGradeMax);
  if (state.sort !== STOCK_SORTS[0]) sp.set('sort', state.sort);
  if (state.page !== DEFAULT_PAGE) sp.set('page', String(state.page));
  if (state.perPage !== DEFAULT_PER_PAGE) sp.set('per_page', String(state.perPage));
  return sp.toString();
}

/** Build the BFF query string for the list endpoint. Mirrors the URL
 * representation 1:1 to keep the BFF mock-friendly for tests. */
export function buildListApiQuery(state: StockListQueryState): string {
  const sp = new URLSearchParams();
  if (state.q.trim()) sp.set('q', state.q.trim());
  if (state.makerEn.length) sp.set('maker_en', csv(state.makerEn) as string);
  if (state.bodytypeCd.length) sp.set('bodytype_cd', csv(state.bodytypeCd) as string);
  if (state.fuelCd.length) sp.set('fuel_cd', csv(state.fuelCd) as string);
  if (state.driveCd.length) sp.set('drive_cd', csv(state.driveCd) as string);
  if (state.missionCd.length) sp.set('mission_cd', csv(state.missionCd) as string);
  if (state.prefName.length) sp.set('pref_name', csv(state.prefName) as string);
  if (state.yearMin !== null) sp.set('year_min', String(state.yearMin));
  if (state.yearMax !== null) sp.set('year_max', String(state.yearMax));
  if (state.priceMin !== null) sp.set('price_min', String(state.priceMin));
  if (state.priceMax !== null) sp.set('price_max', String(state.priceMax));
  if (state.mileageMax !== null) sp.set('mileage_max', String(state.mileageMax));
  if (state.auctionGradeMin) sp.set('auction_grade_min', state.auctionGradeMin);
  if (state.auctionGradeMax) sp.set('auction_grade_max', state.auctionGradeMax);
  sp.set('sort', state.sort);
  sp.set('page', String(state.page));
  sp.set('per_page', String(state.perPage));
  return sp.toString();
}

/* ----------------------------- API calls ------------------------------- */

export async function fetchStockList(
  state: StockListQueryState,
  signal?: AbortSignal,
): Promise<StockListResponse> {
  const qs = buildListApiQuery(state);
  return portalApi<StockListResponse>(`/api/portal/stock/list?${qs}`, { signal });
}

export async function fetchStockDetail(
  vehicleId: string,
  signal?: AbortSignal,
): Promise<StockDetailResponse> {
  return portalApi<StockDetailResponse>(`/api/portal/stock/${encodeURIComponent(vehicleId)}`, {
    signal,
  });
}

/* --------------------------- Display helpers --------------------------- */

/** Format YYYYMM int -> "YYYY-MM" string; nullish -> "—". */
export function formatRegistration(yyyymm: number | null | undefined): string {
  if (yyyymm === null || yyyymm === undefined) return '—';
  const s = String(yyyymm);
  if (s.length === 6) return `${s.slice(0, 4)}-${s.slice(4)}`;
  return s;
}

export function formatMileageKm(km: number | null | undefined): string {
  if (km === null || km === undefined) return '—';
  return `${km.toLocaleString('ja-JP')} km`;
}

export function formatJpy(yen: number | null | undefined): string {
  if (yen === null || yen === undefined) return '—';
  return `¥${yen.toLocaleString('ja-JP')}`;
}

/**
 * Full-width alphanumerics → half-width (e.g. "カスタムＧ" → "カスタムG").
 * Semantics copied from the AS front (SharedInventoryStockItem.vue) so grades
 * render identically on both surfaces.
 */
export function toHalfWidth(str: string | null | undefined): string {
  if (!str) return '';
  return str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
}

/** Reset filters but keep current sort + perPage; return to page 1. */
export function clearedFilters(prev: StockListQueryState): StockListQueryState {
  return {
    ...STOCK_FILTER_DEFAULTS,
    sort: prev.sort,
    perPage: prev.perPage,
    page: 1,
  };
}

export function withPage(state: StockListQueryState, page: number): StockListQueryState {
  return { ...state, page };
}

export function withFilters(
  state: StockListQueryState,
  next: Partial<StockListFilters>,
): StockListQueryState {
  return { ...state, ...next, page: 1 };
}

export function withSort(state: StockListQueryState, sort: StockSort): StockListQueryState {
  return { ...state, sort, page: 1 };
}
